import React from 'react';
import PropTypes from 'prop-types';
import {
    TEST_PLAN_TEMPLATE__SIMPLEX,
    TEST_PLAN_TEMPLATE__DUPLEX,
    CORE_SIZE__SINGLEMODE,
    CORE_SIZE__MULTIMODE_50,
    CORE_SIZE__MULTIMODE_62_5,
    ASSEMBLY_TYPE__SINGLE_CABLE,
    ASSEMBLY_TYPE__MULTI_CABLE,
    TEST_PLAN_TEMPLATE__MULTIFIBER,
    TEST_PLAN_TEMPLATE__FAN_OUT,
    TERMINATION_TYPE__FLAT_CONNECTOR,
    TERMINATION_TYPE__ANGLED_CONNECTOR,
    TERMINATION_TYPE__BARE_FIBER,
    RL_MODE__FAST,
    REFERENCE_ORDER__CHANNEL,
    REFERENCE_ORDER__TEST_TYPE,
    TJ_TYPE__USER_INPUT,
    ASSEMBLY_LENGTH__LT_100_M,
    ASSEMBLY_LENGTH__LT_500_M
} from '@shared/constants/constants';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { InputNumber, Radio, Tooltip } from 'antd';

import './DUTConfigPage.scss';

import { doesNotSupportOrderingReferenceByChannel } from '../lib/referenceOrderUtils';
import { deriveMtjConfiguration } from '../lib/mtjConfiguration';

export class DUTConfigPage extends React.PureComponent {
    static propTypes = {
        wizard: PropTypes.object.isRequired,
        updateWizardData: PropTypes.func.isRequired,
        updateAssembly: PropTypes.func.isRequired,
        location: PropTypes.object.isRequired
    };
    state = {};

    componentDidMount() {}

    handleChangeCoreSize = (e) => {
        let coreSize = e.target.value;

        // Changed from singlemode to multimode or vice versa
        const modeTypeChanged = this.props.wizard.assembly.coreSize === CORE_SIZE__SINGLEMODE || coreSize === CORE_SIZE__SINGLEMODE;

        let newILComp = {};
        let newAssembly = {
            coreSize: coreSize
        };
        let newEquipment = {};
        let newWizardData = {};

        if(coreSize === CORE_SIZE__SINGLEMODE) {
            // IL Comp
            newILComp = {
                attenuation_850: null,
                attenuation_1300: null
            };

            // Assembly
            newAssembly = {
                ...newAssembly,
                referenceOrder: REFERENCE_ORDER__CHANNEL,
            };

            // Wizard Data
            newWizardData = {
                wavelength_850: false,
                wavelength_1300: false,
                wavelength_1310: true,
                wavelength_1490: false,
                wavelength_1550: true,
                wavelength_1625: false,
                assemblyLength: ASSEMBLY_LENGTH__LT_100_M,
            }
        }
        else if(coreSize === CORE_SIZE__MULTIMODE_50 || coreSize === CORE_SIZE__MULTIMODE_62_5) {
            // IL Comp
            newILComp = {
                attenuation_1310: null,
                attenuation_1490: null,
                attenuation_1550: null,
                attenuation_1625: null
            };

            // Assembly
            newAssembly = {
                ...newAssembly,
                referenceOrder: REFERENCE_ORDER__TEST_TYPE,
            };

            // Equipment
            newEquipment = {
                rlMode: RL_MODE__FAST
            };

            // Wizard Data
            newWizardData = {
                returnLossTotal: false
            }

            if(modeTypeChanged) {
                newWizardData = {
                    ...newWizardData,
                    wavelength_850: true,
                    wavelength_1300: true,
                    wavelength_1310: false,
                    wavelength_1490: false,
                    wavelength_1550: false,
                    wavelength_1625: false,
                    assemblyLength: ASSEMBLY_LENGTH__LT_500_M,
                }

                newEquipment = this.checkAndUpdateMTJ1Mode(newEquipment, coreSize, this.props.wizard.assembly.terminationTypeEndA);
            }
        }

        newWizardData = {
            ...newWizardData,
            assembly: {
                ...this.props.wizard.assembly,
                ...newAssembly
            },
            equipment: {
                ...this.props.wizard.equipment,
                ...newEquipment
            },
            ilComp: {
                ...this.props.wizard.ilComp,
                ...newILComp
            }
        };

        this.props.updateWizardData(newWizardData);
    };

    handleChangeTerminationTypeEndA = (e) => {
        const terminationTypeEndA = e.target.value;

        let newEquipment = {};
        let newAssemblyData = {
            terminationTypeEndA: terminationTypeEndA
        };

        newAssemblyData = this.checkAndUpdateReferenceOrder(newAssemblyData, this.props.wizard.assembly.coreSize, terminationTypeEndA);

        newEquipment = this.checkAndUpdateMTJ1Mode(newEquipment, this.props.wizard.assembly.coreSize, terminationTypeEndA);

        let newWizardData = {
            assembly: {
                ...this.props.wizard.assembly,
                ...newAssemblyData
            },
            equipment: {
                ...this.props.wizard.equipment,
                ...newEquipment
            }
        };

        this.props.updateWizardData(newWizardData);
    }

    handleChangeTerminationTypeEndB = (e) => {
        const terminationTypeEndB = e.target.value;

        let newEquipmentData = {};
        let newAssemblyData = {
            terminationTypeEndB: terminationTypeEndB
        };

        if(terminationTypeEndB === TERMINATION_TYPE__BARE_FIBER) {
            newAssemblyData = {
                ...newAssemblyData,
                insertionLossTotal: false,
                returnLossSideB: false,
                insertionLossSideB: false
            };

            // Clearing the End B tests can invalidate the MTJ configuration (e.g. a both-ends or dual
            // setup), so re-derive it from the remaining test selections.
            newEquipmentData = {
                ...newEquipmentData,
                singleTestBlock: false,
                mtjConfiguration: deriveMtjConfiguration({ ...this.props.wizard.assembly, ...newAssemblyData })
            };
        }

        let newWizardData = {
            assembly: {
                ...this.props.wizard.assembly,
                ...newAssemblyData
            },
            equipment: {
                ...this.props.wizard.equipment,
                ...newEquipmentData
            }
        };

        this.props.updateWizardData(newWizardData);
    }

    checkAndUpdateMTJ1Mode(equipment, coreSize, terminationTypeEndA) {
        if(coreSize !== CORE_SIZE__SINGLEMODE && terminationTypeEndA === TERMINATION_TYPE__ANGLED_CONNECTOR) {
            return {
                ...equipment,
                tj1: TJ_TYPE__USER_INPUT
            }
        }
        
        return equipment;
    }

    checkAndUpdateReferenceOrder(assembly, coreSize, terminationTypeEndA) {
        if(doesNotSupportOrderingReferenceByChannel(coreSize, terminationTypeEndA)) {
            return {
                ...assembly,
                referenceOrder: REFERENCE_ORDER__TEST_TYPE
            }
        }

        return assembly;
    }

    render() {
        return (
            <React.Fragment>
                <div className="page dut-config-page">
                    <div className="sidebyside">
                        <div>
                            <div className="section">
                                <div>
                                    <div className="title">Core Size:</div>
                                    <Radio.Group
                                        buttonStyle="solid"
                                        value={this.props.wizard.assembly.coreSize}
                                        onChange={(e) => this.handleChangeCoreSize(e)}
                                    >
                                        <Radio.Button value={CORE_SIZE__SINGLEMODE}>
                                            Singlemode
                                            <br />
                                            9/125&mu;m
                                        </Radio.Button>
                                        <Radio.Button value={CORE_SIZE__MULTIMODE_50}>
                                            Multimode
                                            <br />
                                            50/125&mu;m
                                        </Radio.Button>
                                        <Radio.Button value={CORE_SIZE__MULTIMODE_62_5}>
                                            Multimode
                                            <br />
                                            62.5/125&mu;m
                                        </Radio.Button>
                                    </Radio.Group>
                                </div>
                            </div>

                            {(this.props.wizard.assembly.insertionLossSideA ||
                                this.props.wizard.assembly.insertionLossSideB ||
                                this.props.wizard.assembly.returnLossSideA ||
                                this.props.wizard.assembly.returnLossSideB ||
                                this.props.wizard.assembly.returnLossTotal ||
                                this.props.wizard.assembly.insertionLossTotal) && (
                                <React.Fragment>
                                    <div className="section cable">
                                        <div>
                                            <div className="title">Hybrid Assembly:</div>
                                            <Radio.Group
                                                buttonStyle="solid"
                                                value={this.props.wizard.hybridAssembly}
                                                disabled={
                                                    this.props.wizard.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT ||
                                                    (this.props.wizard.assembly.insertionLossSideA === true &&
                                                        this.props.wizard.assembly.insertionLossSideB === false &&
                                                        this.props.wizard.assembly.returnLossSideA === true &&
                                                        this.props.wizard.assembly.returnLossSideB === false) ||
                                                    (this.props.wizard.assembly.insertionLossSideA === false &&
                                                        this.props.wizard.assembly.insertionLossSideB === true &&
                                                        this.props.wizard.assembly.returnLossSideA === false &&
                                                        this.props.wizard.assembly.returnLossSideB === true)
                                                }
                                                onChange={(e) => this.props.updateWizardData({ hybridAssembly: e.target.value })}
                                            >
                                                <Tooltip
                                                    placement="top"
                                                    title={<div className="tooltip">End A type &ne; End B type</div>}
                                                >
                                                    <Radio.Button value={true}>Yes</Radio.Button>
                                                </Tooltip>
                                                <Tooltip
                                                    placement="top"
                                                    title={<div className="tooltip">End A type = End B type</div>}
                                                >
                                                    <Radio.Button value={false}>No</Radio.Button>
                                                </Tooltip>
                                            </Radio.Group>
                                        </div>
                                    </div>
                                </React.Fragment>
                            )}
                            <div className="termination-row">
                                <div className="section">
                                    <div className="title">End A Termination Type:</div>
                                    <Form.Item
                                        validateStatus={
                                            this.props.wizard.validation.fields.terminationTypeEndA ? '' : 'error'
                                        }
                                        help={
                                            this.props.wizard.validation.fields.terminationTypeEndA
                                                ? ''
                                                : 'Must select End A termination type'
                                        }
                                    >
                                        <Radio.Group
                                            buttonStyle="solid"
                                            defaultValue={this.props.wizard.assembly.terminationTypeEndA}
                                            onChange={(e) => this.handleChangeTerminationTypeEndA(e)}
                                        >
                                            <Tooltip placement="top" title="End A is terminated with a flat connector">
                                                <Radio.Button value={TERMINATION_TYPE__FLAT_CONNECTOR}>Flat</Radio.Button>
                                            </Tooltip>
                                            <Tooltip placement="top" title="End A is terminated with an angled connector">
                                                <Radio.Button value={TERMINATION_TYPE__ANGLED_CONNECTOR}>Angled</Radio.Button>
                                            </Tooltip>
                                        </Radio.Group>
                                    </Form.Item>
                                </div>
                                <div className="section ">
                                    <div className="title">End B Termination Type:</div>
                                    <Form.Item
                                        validateStatus={
                                            this.props.wizard.validation.fields.terminationTypeEndB ? '' : 'error'
                                        }
                                        help={
                                            this.props.wizard.validation.fields.terminationTypeEndB
                                                ? ''
                                                : 'Must select End B termination type'
                                        }
                                    >
                                        <Radio.Group
                                            buttonStyle="solid"
                                            value={this.props.wizard.assembly.terminationTypeEndB}
                                            onChange={(e) => this.handleChangeTerminationTypeEndB(e)}
                                        >
                                            <Tooltip placement="top" title="End B is terminated with a flat connector">
                                                <Radio.Button value={TERMINATION_TYPE__FLAT_CONNECTOR}>Flat</Radio.Button>
                                            </Tooltip>
                                            <Tooltip placement="top" title="End B is terminated with an angled connector">
                                                <Radio.Button value={TERMINATION_TYPE__ANGLED_CONNECTOR}>Angled</Radio.Button>
                                            </Tooltip>
                                            <Tooltip placement="top" title="End B is not terminated">
                                                <Radio.Button value={TERMINATION_TYPE__BARE_FIBER}>Bare Fiber</Radio.Button>
                                            </Tooltip>
                                        </Radio.Group>
                                    </Form.Item>
                                </div>
                            </div>
                            <div className="section ">
                                <div className="title">Assembly Type:</div>
                                <Radio.Group
                                    buttonStyle="solid"
                                    value={this.props.wizard.assemblyType}
                                    onChange={(e) => this.props.updateWizardData({ assemblyType: e.target.value })}
                                >
                                    <Radio.Button value={ASSEMBLY_TYPE__SINGLE_CABLE}>Single Cable</Radio.Button>
                                    <Tooltip placement="right" title="Multi Cable is a bundle of Single Cables">
                                        <Radio.Button value={ASSEMBLY_TYPE__MULTI_CABLE}>Multi Cable</Radio.Button>
                                    </Tooltip>
                                </Radio.Group>
                            </div>
                            {this.props.wizard.assemblyType === ASSEMBLY_TYPE__MULTI_CABLE && (
                                <div className="section">
                                    <div className="title"> Cables Per Assembly:</div>
                                    <div>
                                        <Form.Item
                                            validateStatus={
                                                this.props.wizard.validation.fields.cablesPerAssembly ? '' : 'error'
                                            }
                                            help={
                                                this.props.wizard.validation.fields.cablesPerAssembly
                                                    ? ''
                                                    : 'Must be an integer greater than 0'
                                            }
                                        >
                                            <InputNumber
                                                onChange={(value) => {
                                                    this.props.updateWizardData({ cablesPerAssembly: value });
                                                }}
                                                formatter={(value) => `${value.replace(/^[A-Za-z--]+$/, '')}`}
                                                defaultValue={this.props.wizard.cablesPerAssembly}
                                                min={1}
                                                max={9999}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>
                            )}
                            {
                                this.props.wizard.testPlanTemplate !== TEST_PLAN_TEMPLATE__SIMPLEX &&
                                this.props.wizard.testPlanTemplate !== TEST_PLAN_TEMPLATE__DUPLEX && (
                                    <div className="section">
                                        <div className="title">Fibers per Cable:</div>
                                        <Form.Item
                                            validateStatus={this.props.wizard.validation.fields.fibersPerCable ? '' : 'error'}
                                            help={
                                                this.props.wizard.validation.fields.fibersPerCable
                                                    ? ''
                                                    : 'Must be an integer greater than 0'
                                            }
                                        >
                                            <InputNumber
                                                onChange={(value) => {
                                                    this.props.updateWizardData({ fibersPerCable: value });
                                                }}
                                                defaultValue={this.props.wizard.fibersPerCable}
                                                formatter={(value) => `${value.replace(/^[A-Za-z--]+$/, '')}`}
                                                min={1}
                                                max={
                                                    (this.props.wizard.testPlanTemplate === TEST_PLAN_TEMPLATE__MULTIFIBER ||
                                                        this.props.wizard.testPlanTemplate === TEST_PLAN_TEMPLATE__FAN_OUT) &&
                                                    this.props.wizard.hybridAssembly &&
                                                    this.props.wizard.equipment.singleTestBlock
                                                        ? 180
                                                        : 360
                                                }
                                            />
                                        </Form.Item>
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}
